<template>
    <!-- Modern Friend Card -->
    <div class="flex flex-col h-full">
        <!-- Header Section -->
        <div class="p-4 pb-2">
            <h2 class="text-lg font-semibold text-gray-900 truncate capitalize mb-2">
                {{ friend.name.toLowerCase() }}
            </h2>
        </div>

        <!-- Info Section -->
        <div class="px-4 pb-4 flex-1 space-y-2">
            <!-- Birthday -->
            <div v-if="friend.birthday" class="flex items-center text-sm">
                <div class="w-5 h-5 mr-2 flex-shrink-0">
                    <svg
class="w-5 h-5 text-pink-500 lucide lucide-cake-icon lucide-cake"
                        xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
>
                        <path d="M20 21v-8a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8" />
                        <path d="M4 16s.5-1 2-1 2.5 2 4 2 2.5-2 4-2 2.5 2 4 2 2-1 2-1" />
                        <path d="M2 21h20" />
                        <path d="M7 8v3" />
                        <path d="M12 8v3" />
                        <path d="M17 8v3" />
                        <path d="M7 4h.01" />
                        <path d="M12 4h.01" />
                        <path d="M17 4h.01" />
                    </svg>
                </div>
                <div class="min-w-0">
                    <p v-if="daysUntilNextAnniversary(friend.birthday) < 1" class="text-red-600 font-semibold">
                        Birthday today! 🎉
                    </p>
                    <p v-else-if="daysUntilNextAnniversary(friend.birthday) < 2" class="text-red-600 font-semibold">
                        Birthday tomorrow
                    </p>
                    <p v-else-if="daysUntilNextAnniversary(friend.birthday) < 14" class="text-red-600 font-medium">
                        Birthday in {{ daysUntilNextAnniversary(friend.birthday) }} days
                    </p>
                    <p v-else class="text-gray-600">
                        Birthday in {{ daysUntilNextAnniversary(friend.birthday) }} days
                    </p>
                </div>
            </div>

            <!-- Anniversary -->
            <div v-if="friend.anniversary" class="flex items-center text-sm">
                <div class="w-5 h-5 mr-2 flex-shrink-0">
                    <svg
class="w-5 h-5 text-purple-500 lucide lucide-heart-icon lucide-heart"
                        xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
>
                        <path
                            d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"
/>
                    </svg>
                </div>
                <div class="min-w-0">
                    <p v-if="daysUntilNextAnniversary(friend.anniversary) < 1" class="text-red-600 font-semibold">
                        Anniversary today! 💖
                    </p>
                    <p v-else-if="daysUntilNextAnniversary(friend.anniversary) < 2" class="text-red-600 font-semibold">
                        Anniversary tomorrow
                    </p>
                    <p v-else-if="daysUntilNextAnniversary(friend.anniversary) < 14" class="text-red-600 font-medium">
                        Anniversary in {{ daysUntilNextAnniversary(friend.anniversary) }} days
                    </p>
                    <p v-else class="text-gray-600">
                        Anniversary in {{ daysUntilNextAnniversary(friend.anniversary) }} days
                    </p>
                </div>
            </div>

            <!-- Email -->
            <div v-if="friend.email" class="flex items-center text-sm">
                <div class="w-5 h-5 mr-2 flex-shrink-0">
                    <svg
class="w-5 h-5 text-blue-500 lucide lucide-mail-icon lucide-mail"
                        xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
>
                        <path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7" />
                        <rect x="2" y="4" width="20" height="16" rx="2" />
                    </svg>
                </div>
                <p class="text-gray-600 truncate">
                    {{ friend.email.toLowerCase() }}
                </p>
            </div>
        </div>

        <!-- Action Buttons -->
        <div class="p-4 pt-0 border-t border-gray-100">
            <div class="grid grid-cols-3 gap-2">
                <button
type="button" @click="$router.push({ name: 'PeopleAssign', params: { id: friend.id } })"
                    class="px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
>
                    Gifts
                </button>

                <button
type="button" @click="$router.push({ name: 'PeopleEdit', params: { id: friend.id } })"
                    class="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
>
                    Edit
                </button>

                <button
type="button" @click="deleteFriend()"
                    class="px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
>
                    Delete
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { API } from "aws-amplify";

import { getFriend } from '../graphql/queries';

import { deleteFriend, deleteGiftFriend } from '../graphql/mutations';

export default {
    name: "FriendItem",
    props: {
        friend: {
            type: Object,
            default() {
                return {}
            }
        },
    },
    data() {
        return {
            form: {},
            image: "",
            showModal: false,
            imageNum: "",
        };
    },
    setup() { },
    created() {
    },
    computed: {
        friends() {
            return this.$store.state.friendMap;
        },
        friendImage() {
            return this.image
        }
    },
    methods: {
        daysUntilNextAnniversary(dob) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const birthday = new Date(dob);

            let nextBirthday = new Date(today.getFullYear(), birthday.getMonth(), birthday.getDate());

            if (today > nextBirthday) {
                nextBirthday.setFullYear(nextBirthday.getFullYear() + 1);
            }


            if (nextBirthday === today) {
                return 0;
            };

            const diffMillis = nextBirthday.getTime() - today.getTime();

            return Math.ceil(diffMillis / (1000 * 60 * 60 * 24));
        },

        async deleteFriend() {
            if (confirm("Are you sure you want to delete " + this.friend.name + "?")) {
                try {

                    let friendLinks = await API.graphql({
                        query: getFriend,
                        variables: { id: this.friend.id }
                    });

                    try {
                        friendLinks.data.getFriend.GiftLink.items.forEach(function (item, index) {
                            const mutationInput = { "id": item.id };
                            API.graphql({ query: deleteGiftFriend, variables: { input: mutationInput }, });
                        });
                    } catch (error) {
                    };

                } catch (error) {
                };

                try {
                    var response = await API.graphql({ query: deleteFriend, variables: { input: { id: this.friend.id } } });
                } catch (error) {
                    console.log('Error deleting friend...', error)
                };
            } else {
                return;
            }

        },
    },
};
</script>